import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { AbstractService } from "../../abstractions/abstract.service";
import { Globals } from "../../app.globals";

@Injectable()
export class DrawingRegisterService extends AbstractService {
    projectId: number = 0;

    constructor(public http: HttpClient, public globals: Globals) {
        super(http, globals);
    }

    save(entity: DrawingRegister): Observable<number> {
        this.showSpinner();
        return this.http
            .post(this.getApiUrl(), entity, { responseType: "text" })
            .pipe(
                map((res) => parseInt(res)),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getPrimaryFiles(
        projectId: number,
        aggregateId: string
    ): Observable<DrawingRegisterAttachment[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/primaryFiles/" + aggregateId)
            .pipe(
                map((res) => res as DrawingRegisterAttachment[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getSecondaryFiles(
        projectId: number,
        aggregateId: string
    ): Observable<DrawingRegisterAttachment[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/secondaryFiles/" + aggregateId)
            .pipe(
                map((res) => res as DrawingRegisterAttachment[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getAllFilesForEntity(
        projectId: number,
        aggregateId: string
    ): Observable<DrawingRegisterAttachment[]> {
        // this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/getAllFiles/" + aggregateId)
            .pipe(
                map((res) => res as DrawingRegisterAttachment[]),
                catchError(this.handleError),
                finalize(() => {})
            );
    }

    uploadFile(formData: FormData): Observable<any> {
        this.showSpinner();
        return this.http.put(this.getApiUrl() + "/upload", formData).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    uploadFiles(
        url: string,
        formData: FormData,
        headers: HttpHeaders | { [header: string]: string | string[] } = null
    ) {
        return this.http.post(url, formData, { headers: headers }).pipe(
            map((res) => res),
            catchError(this.handleError)
        );
    }

    getApiUrl(): string {
        return "api/projects/" + this.projectId + "/sitework/drawingregister";
    }

    deleteFile(fileName): Observable<void> {
        this.showSpinner();

        return this.http
            .request("delete", this.getApiUrl() + "/file/delete", {
                body: { url: fileName },
            })
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getAggregateId(): Observable<string> {
        this.showSpinner();
        return this.http.get(this.getApiUrl() + "/aggregateId").pipe(
            map((res) => {
                return res;
            }),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getByEntryNumber(entryNumber: number): Observable<DrawingRegister> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/byEntryNumber/" + entryNumber)
            .pipe(
                map((res) => res as DrawingRegister),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getByName(name: string): Observable<DrawingRegister> {
        this.showSpinner();
        return this.http.get(this.getApiUrl() + "/byName/" + name).pipe(
            map((res) => res as DrawingRegister),
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }
}

export class DrawingRegister {
    id: number;
    projectId: number;
    aggregateId: string;
    entryNumber: number;
    revision: string;
    project: any;
    projectNameCode: string;
    projectLogoUrl: string;
    name: string;
    description: string;
    type: string;
    source: string;
    structuredRefs: string[];
    levels: string[];
    packages: string[];
    elevations: string[];
    cores: string[];
    drawingRegisterStatus: string;
    elements: string[];
    drawingRegisterSubContractorContactsIds: number[];
    subcontractors: string[];
    drawingRegisterClientConsultantContactsIds: number[];
    clientsConsultantsContacts: string[];
    drawingRegisterSupplierContactsIds: number[];
    suppliers: string[];
    drawingRegisterAttachments: DrawingRegisterAttachment[];
    employeesIds: number[];
    employees: string[];
    notes: string;
    lastUpdateDate: Date;
    lastUpdatedById: number;
    lastUpdatedByFullName: string;
    dateTimeIssued: Date;
    status: string;
    clientName: string;
    organization: string;

    static map(entity: any): DrawingRegister {
        if (!entity) return new DrawingRegister();

        var subContractorPackageAttachments: DrawingRegisterAttachment[] = [];

        (entity.subContractorPackageAttachments || []).forEach(function (
            attachment
        ) {
            subContractorPackageAttachments.push(
                DrawingRegisterAttachment.map(attachment)
            );
        });

        return {
            aggregateId: entity.aggregateId,
            clientName: entity.clientName,
            clientsConsultantsContacts: entity.clientsConsultantsContacts,
            dateTimeIssued: entity.dateTimeIssued,
            description: entity.description,
            drawingRegisterAttachments: entity.drawingRegisterAttachments,
            drawingRegisterClientConsultantContactsIds:
                entity.drawingRegisterClientConsultantContactsIds,
            drawingRegisterSubContractorContactsIds:
                entity.drawingRegisterSubContractorContactsIds,
            drawingRegisterSupplierContactsIds:
                entity.drawingRegisterSupplierContactsIds,
            elements: entity.elements,
            employees: entity.employees,
            employeesIds: entity.employeesIds,
            entryNumber: entity.entryNumber,
            id: entity.id,
            lastUpdateDate: entity.lastUpdateDate,
            lastUpdatedByFullName: entity.lastUpdatedByFullName,
            lastUpdatedById: entity.lastUpdatedById,
            levels: entity.levels,
            name: entity.name,
            notes: entity.notes,
            organization: entity.organization,
            packages: entity.packages,
            project: entity.project,
            projectId: entity.projectId,
            projectLogoUrl: entity.projectLogoUrl,
            projectNameCode: entity.projectNameCode,
            revision: entity.revision,
            source: entity.source,
            status: entity.status,
            drawingRegisterStatus: entity.drawingRegisterStatus,
            structuredRefs: entity.structuredRefs,
            subcontractors: entity.subcontractors,
            suppliers: entity.suppliers,
            type: entity.type,
            cores: entity.cores,
            elevations: entity.elevations,
        };
    }
}

export class DrawingRegisterAttachment {
    id: number;
    drawingRegisterId: number;
    type: string;
    drawingRegisterType: string;
    revision: string;
    fileName: string;
    originalFileName: string;
    fileUrl: string;
    added: Date;
    addedById: number;
    addedByFullName: string;
    notes: string;
    status: string;
    recipients: string;
    newAttachment: boolean = false;
    isUploaded: boolean = false;

    static map(entity: any): DrawingRegisterAttachment {
        if (!entity) return new DrawingRegisterAttachment();

        return {
            added: entity.added,
            addedByFullName: entity.addedByFullName,
            addedById: entity.addedById,
            drawingRegisterType: entity.drawingRegisterType,
            drawingRegisterId: entity.drawingRegisterId,
            fileName: entity.fileName,
            fileUrl: entity.fileUrl,
            id: entity.id,
            isUploaded: entity.isUploaded,
            newAttachment: entity.newAttachment,
            notes: entity.notes,
            originalFileName: entity.originalFileName,
            recipients: entity.recipients,
            revision: entity.revision,
            status: entity.status,
            type: entity.type,
        };
    }
}
